import { useState } from "react";
import { logoUrl } from "@/api/branding";
import { OraiLogo } from "@/components/brand/OraiLogo";
import { usePlatformBrand } from "@/hooks/usePlatformBranding";
import type { ColorMode } from "@/lib/branding";
import { cn } from "@/lib/cn";
import { useUIStore } from "@/store/useUIStore";

/**
 * ORAI's OWN marks, as a platform admin set them on /admin/branding -- or the artwork built into
 * the app wherever a slot is empty, still loading, or fails to load, so a broken upload never
 * leaves an empty box. Nothing here is a tenant's: a white-labelled workspace's logo is
 * `BrandMark`, which falls back to `PlatformNavMark`.
 */

interface ImageTileProps {
  src: string;
  size: number;
  alt?: string | undefined;
  className?: string | undefined;
  onError?: (() => void) | undefined;
}

/** An uploaded image on a white tile. Logos are drawn for white, and the tile keeps a transparent
 *  one legible on every surface: light, dark, or a tenant's own colours. */
export function ImageTile({ src, size, alt = "", className, onError }: ImageTileProps) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-[var(--radius-ctl)] bg-white ring-1 ring-black/5",
        className,
      )}
      style={{ width: size, height: size }}
    >
      <img src={src} alt={alt} className="size-full object-contain p-[12%]" onError={onError} />
    </span>
  );
}

/** The built-in mark, drawn in the brand's button colour. */
export function BuiltInIcon({ size, className }: { size: number; className?: string | undefined }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex shrink-0 items-center justify-center rounded-[var(--radius-ctl)] bg-brand-button text-brand-on-button",
        className,
      )}
      style={{ width: size, height: size }}
    >
      <OraiLogo size={Math.round(size * 0.56)} title="" />
    </span>
  );
}

/** ORAI's square mark: the uploaded icon, or the built-in one. Decorative -- name the brand nearby. */
export function PlatformIcon({ size = 32, className }: { size?: number; className?: string | undefined }) {
  const { icon_path } = usePlatformBrand();
  const src = logoUrl(icon_path);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (src && failedSrc !== src) {
    return <ImageTile src={src} size={size} className={className} onError={() => setFailedSrc(src)} />;
  }
  return <BuiltInIcon size={size} className={className} />;
}

/** The uploaded logo for `mode` (the viewer's own when omitted): the dark-mode logo in dark mode
 *  when there is one, else the logo. Null when no logo was uploaded. */
function usePlatformLogoSrc(modeOverride?: ColorMode): string | null {
  const brand = usePlatformBrand();
  const viewerMode = useUIStore((s) => s.mode);
  const mode = modeOverride ?? viewerMode;
  return logoUrl(mode === "dark" ? (brand.logo_dark_path ?? brand.logo_path) : brand.logo_path);
}

interface PlatformNavMarkProps {
  /** The box the mark must fit. The navigation rail is 56px wide, so a wide logo gets 48px. */
  maxWidth?: number;
  height?: number;
  /** Preview a mode other than the viewer's own. */
  mode?: ColorMode | undefined;
}

/**
 * ORAI's mark at the top of a workspace's navigation: the LOGO, fitted to the rail -- a full logo
 * reads there, where an icon squeezed into a 32px square did not -- or the icon when no logo was
 * uploaded. The icon's own job is the browser tab.
 */
export function PlatformNavMark({ maxWidth = 48, height = 32, mode }: PlatformNavMarkProps) {
  const src = usePlatformLogoSrc(mode);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt=""
        className="block object-contain"
        style={{ maxWidth, maxHeight: height }}
        onError={() => setFailedSrc(src)}
      />
    );
  }
  return <PlatformIcon size={height} />;
}

interface PlatformWordmarkProps {
  /** Height of the logo, or of the icon when there is no logo. */
  height?: number;
  className?: string | undefined;
  iconClassName?: string | undefined;
  nameClassName?: string;
}

/** ORAI's full lockup: the uploaded logo (its dark version in dark mode), or the icon + the name. */
export function PlatformWordmark({
  height = 36,
  className,
  iconClassName,
  nameClassName = "text-[14px] font-semibold text-brand-text-primary",
}: PlatformWordmarkProps) {
  const brand = usePlatformBrand();
  const src = usePlatformLogoSrc();
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt={brand.brand_name}
        style={{ height }}
        className={cn("w-auto max-w-[220px] object-contain", className)}
        onError={() => setFailedSrc(src)}
      />
    );
  }
  return (
    <span className={cn("flex items-center gap-2.5", className)}>
      <PlatformIcon size={height} className={iconClassName} />
      <span className={nameClassName}>{brand.brand_name}</span>
    </span>
  );
}
